import axiosInstance from '../helpers/axios';

/**
 * /api/ops/cases — the CX inbox beyond list/detail/status: the portal
 * owner (beside Salesforce's OwnerId, never over it), relating a case to
 * the class and account it is about, the triage filters, and escalation.
 */

export const CASE_ORIGINS = [
  'CX Inbox',
  'Email',
  'Portal',
  'Web',
  'Phone',
  'Salesforce',
  'Academy X',
  'Anytime',
];

export type TOpsCaseRow = {
  id: string;
  caseNumber: string | null;
  subject: string | null;
  status: string | null;
  origin: string | null;
  reason: string | null;
  priority: string | null;
  createdDate: string | null;
  contactName: string | null;
  accountId: string | null;
  closed: boolean;
  timeInStageHours: number | null;
  localOrigin: boolean;
  ownerEmail: string | null;
  ownerName: string | null;
  classId: string | null;
  className: string | null;
  classStart: string | null;
  escalated: boolean;
  /** How the intake classified the mail that opened it: customer | notice; null for other origins. */
  intakeKind: string | null;
};

export type TOpsCasePage = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsCaseRow[];
};

export type TOpsCaseClass = {
  id: string;
  name: string | null;
  start: string | null;
  end: string | null;
  status: string | null;
  instructor: string | null;
  accountId: string | null;
};

export type TOpsCaseFull = {
  id: string;
  caseNumber: string | null;
  subject: string | null;
  description: string | null;
  status: string | null;
  origin: string | null;
  priority: string | null;
  createdDate: string | null;
  contactName: string | null;
  contactEmail: string | null;
  accountId: string | null;
  accountName: string | null;
  closed: boolean;
  localOrigin: boolean;
  salesforceOwnerId: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  classId: string | null;
  class: TOpsCaseClass | null;
  relatedCases: {
    id: string;
    caseNumber: string | null;
    subject: string | null;
    status: string | null;
    createdDate: string | null;
    closed: boolean;
  }[];
  escalatedAt: string | null;
  intakeKind: string | null;
  escalatedBy: string | null;
  escalationReason: string | null;
  requested: {
    courseName: string | null;
    date: string | null;
    time: string | null;
  };
};

export type TOpsCasePerson = { email: string; name: string };

export type TOpsCaseClassHit = {
  id: string;
  name: string | null;
  start: string | null;
  status: string | null;
  instructor: string | null;
  type: string | null;
  accountId: string | null;
  accountName: string | null;
  onAccount: boolean;
};

export type CaseFilters = {
  /** unset = hide machine mail · 'machine' · 'all' · or one intake kind */
  kind?: string;
  q?: string;
  status?: string;
  owner?: string; // 'me' | 'none' | an email
  origin?: string;
  classId?: string;
  all?: boolean;
  page?: number;
  accountId?: string;
  programId?: string;
};

export const getOpsCasePage = async (f: CaseFilters) =>
  (
    await axiosInstance.get<TOpsCasePage>('ops/cases', {
      params: { ...f, all: f.all ? 'true' : undefined },
    })
  ).data;

export const getOpsCaseFull = async (id: string) =>
  (await axiosInstance.get<TOpsCaseFull>(`ops/cases/${id}`)).data;

export const getOpsCasePeople = async () =>
  (await axiosInstance.get<{ items: TOpsCasePerson[] }>('ops/cases/people'))
    .data.items;

export const searchOpsCaseClasses = async (
  q: string,
  accountId?: string | null,
) =>
  (
    await axiosInstance.get<{ items: TOpsCaseClassHit[] }>(
      'ops/cases/classes',
      { params: { q, accountId: accountId ?? undefined } },
    )
  ).data.items;

export const setOpsCaseOwner = async (id: string, ownerEmail: string | null) =>
  (
    await axiosInstance.patch<{ id: string; ownerEmail: string | null }>(
      `ops/cases/${id}/owner`,
      { ownerEmail },
    )
  ).data;

export const relateOpsCase = async (
  id: string,
  body: { classId?: string | null; accountId?: string | null },
) =>
  (
    await axiosInstance.patch<{
      id: string;
      classId: string | null;
      accountId: string | null;
    }>(`ops/cases/${id}/relate`, body)
  ).data;

export const escalateOpsCase = async (id: string, reason: string) =>
  (
    await axiosInstance.post<{
      id: string;
      priority: string;
      escalatedAt: string;
    }>(`ops/cases/${id}/escalate`, { reason })
  ).data;

// ---------------------------------------------------------------- replies

export type TOpsCaseMessage = {
  id: string;
  direction: 'in' | 'out';
  /** Intake classification of an inbound message; null for outbound and older rows. */
  kind: string | null;
  at: string | null;
  receivedAt: string | null;
  from: string | null;
  fromName: string | null;
  to: string[];
  cc: string[];
  subject: string | null;
  text: string | null;
  hasAttachments: boolean;
  mailbox: string | null;
  actorEmail: string | null;
  via: 'email' | 'portal' | 'outlook' | string;
  outboxId: string | null;
  status: string | null;
  holdReason: string | null;
  error: string | null;
  delivery: 'draft' | 'send' | null;
  webLink: string | null;
};

export type TOpsCaseRecipients = {
  caseId: string;
  ourMailboxes: string[];
  customer: { name: string | null; to: string[]; cc: string[] };
  salesRep: { name: string | null; email: string | null } | null;
  trainers: {
    name: string;
    email: string | null;
    source: 'identity' | 'login' | null;
  }[];
  className: string | null;
  replyMode: 'draft' | 'send';
  transport: {
    mode: 'dry-run' | 'live';
    transport: string;
    configured: boolean;
    from: string;
    redirectTo: string | null;
  };
};

export type TOpsCaseReplyBody = {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body: string;
  delivery?: 'draft' | 'send';
  inReplyTo?: string | null;
  internalCopy?: boolean;
  thenStatus?: string | null;
};

export type TOpsCaseReplyResult = {
  caseId: string;
  messageId: string;
  outboxId: string;
  status: string;
  holdReason: string | null;
  error: string | null;
  delivery: 'draft' | 'send';
  webLink: string | null;
  subject: string;
  to: string[];
  cc: string[];
  inThread: boolean;
  caseStatus: string | null;
};

export const getOpsCaseThread = async (id: string) =>
  (
    await axiosInstance.get<{ caseId: string; items: TOpsCaseMessage[] }>(
      `ops/cases/${id}/thread`,
    )
  ).data.items;

export const getOpsCaseRecipients = async (id: string) =>
  (await axiosInstance.get<TOpsCaseRecipients>(`ops/cases/${id}/recipients`))
    .data;

export const replyOpsCase = async (id: string, body: TOpsCaseReplyBody) =>
  (await axiosInstance.post<TOpsCaseReplyResult>(`ops/cases/${id}/reply`, body))
    .data;

/** Open a case from a class or an account (staff side). */
export const createOpsCase = async (body: {
  subject: string;
  description?: string | null;
  classId?: string | null;
  accountId?: string | null;
  status?: string | null;
  ownerEmail?: string | null;
  priority?: string | null;
}) =>
  (
    await axiosInstance.post<{
      id: string;
      caseNumber: string;
      status: string;
      accountId: string | null;
      classId: string | null;
      ownerEmail: string;
    }>('ops/cases', body)
  ).data;
